import { useState } from 'react';
import { Sparkles, AlertTriangle, Lightbulb, Building2, MapPin, Megaphone, Search, Quote, Route, GalleryHorizontal } from 'lucide-react';
import { useSiteContentValue } from '@/hooks/useSiteContentValue';
import { VideoField } from '@/components/admin/VideoField';
import { SectionPageEditor } from '@/components/admin/SectionPageEditor';
import type { SectionDef } from '@/components/admin/SectionPageEditor';

// Every field maps to a real i18n key already rendered on the live Brand
// Story page (all 6 section components under pages/brand-story/components
// are actually imported by page.tsx — no dead files here, unlike Home).
// All 59 brandStory.* keys in the locale bundle are used somewhere below;
// none are orphaned. common.shopNow/common.whereToBuy (used by the CTA
// section's buttons) are skipped — shared site-wide strings, not specific
// to this page.
const BRAND_STORY_SECTIONS: SectionDef[] = [
  {
    key: 'hero',
    title: 'Hero',
    icon: Sparkles,
    texts: [
      { key: 'brandStory.hero.label', label: 'Eyebrow Label' },
      { key: 'brandStory.hero.heading', label: 'Heading' },
      { key: 'brandStory.hero.subheading', label: 'Sub-heading' },
      { key: 'brandStory.hero.subtitle', label: 'Subtitle' },
    ],
  },
  {
    key: 'problem',
    title: 'The Problem',
    icon: AlertTriangle,
    images: [{ key: 'brandStory.problem.image', label: 'Section Photo' }],
    texts: [
      { key: 'brandStory.problem.label', label: 'Eyebrow Label' },
      { key: 'brandStory.problem.heading', label: 'Heading' },
      { key: 'brandStory.problem.p1', label: 'Paragraph 1', long: true },
      { key: 'brandStory.problem.p2', label: 'Paragraph 2', long: true },
      { key: 'brandStory.problem.p3', label: 'Paragraph 3', long: true },
      { key: 'brandStory.problem.stat1', label: 'Stat 1 — Value' },
      { key: 'brandStory.problem.stat1Label', label: 'Stat 1 — Label' },
      { key: 'brandStory.problem.stat2', label: 'Stat 2 — Value' },
      { key: 'brandStory.problem.stat2Label', label: 'Stat 2 — Label' },
      { key: 'brandStory.problem.stat3', label: 'Stat 3 — Value' },
      { key: 'brandStory.problem.stat3Label', label: 'Stat 3 — Label' },
    ],
  },
  {
    key: 'solution',
    title: 'Our Solution',
    icon: Lightbulb,
    note: 'The 3 feature icons are fixed in code — only the text below is editable here.',
    texts: [
      { key: 'brandStory.solution.label', label: 'Eyebrow Label' },
      { key: 'brandStory.solution.heading', label: 'Heading' },
      { key: 'brandStory.solution.body', label: 'Body Text', long: true },
      { key: 'brandStory.solution.feature1.title', label: 'Feature 1 — Title' },
      { key: 'brandStory.solution.feature1.point1', label: 'Feature 1 — Point 1' },
      { key: 'brandStory.solution.feature1.point2', label: 'Feature 1 — Point 2' },
      { key: 'brandStory.solution.feature1.point3', label: 'Feature 1 — Point 3' },
      { key: 'brandStory.solution.feature1.point4', label: 'Feature 1 — Point 4' },
      { key: 'brandStory.solution.feature2.title', label: 'Feature 2 — Title' },
      { key: 'brandStory.solution.feature2.point1', label: 'Feature 2 — Point 1' },
      { key: 'brandStory.solution.feature2.point2', label: 'Feature 2 — Point 2' },
      { key: 'brandStory.solution.feature2.point3', label: 'Feature 2 — Point 3' },
      { key: 'brandStory.solution.feature2.point4', label: 'Feature 2 — Point 4' },
      { key: 'brandStory.solution.feature3.title', label: 'Feature 3 — Title' },
      { key: 'brandStory.solution.feature3.point1', label: 'Feature 3 — Point 1' },
      { key: 'brandStory.solution.feature3.point2', label: 'Feature 3 — Point 2' },
      { key: 'brandStory.solution.feature3.point3', label: 'Feature 3 — Point 3' },
      { key: 'brandStory.solution.feature3.point4', label: 'Feature 3 — Point 4' },
    ],
  },
  {
    key: 'company',
    title: 'The Company',
    icon: Building2,
    note: 'The 3 stat icons are fixed in code — only the text/images below are editable here.',
    images: [
      { key: 'brandStory.company.productImage', label: 'Product Photo' },
      { key: 'brandStory.company.logoBadge', label: 'Small Logo Badge' },
    ],
    texts: [
      { key: 'brandStory.company.label', label: 'Eyebrow Label' },
      { key: 'brandStory.company.heading', label: 'Heading' },
      { key: 'brandStory.company.p1', label: 'Paragraph 1', long: true },
      { key: 'brandStory.company.p2', label: 'Paragraph 2', long: true },
      { key: 'brandStory.company.signatureName', label: 'Signature — Cursive Name' },
      { key: 'brandStory.company.signatureCaption', label: 'Signature — Caption Line' },
      { key: 'brandStory.company.stat1', label: 'Stat 1 — Value' },
      { key: 'brandStory.company.stat1Label', label: 'Stat 1 — Label' },
      { key: 'brandStory.company.stat2', label: 'Stat 2 — Value' },
      { key: 'brandStory.company.stat2Label', label: 'Stat 2 — Label' },
      { key: 'brandStory.company.stat3', label: 'Stat 3 — Value' },
      { key: 'brandStory.company.stat3Label', label: 'Stat 3 — Label' },
    ],
  },
  {
    key: 'madeFor',
    title: 'Made For Bangladesh',
    icon: MapPin,
    note: 'The 4 concern icons are fixed in code — only the text below is editable here.',
    texts: [
      { key: 'brandStory.madeFor.label', label: 'Eyebrow Label' },
      { key: 'brandStory.madeFor.heading', label: 'Heading' },
      { key: 'brandStory.madeFor.p1', label: 'Paragraph 1', long: true },
      { key: 'brandStory.madeFor.p2', label: 'Paragraph 2', long: true },
      { key: 'brandStory.madeFor.cta', label: 'Button' },
      { key: 'brandStory.madeFor.concern1.title', label: 'Concern 1 — Title' },
      { key: 'brandStory.madeFor.concern1.desc', label: 'Concern 1 — Description' },
      { key: 'brandStory.madeFor.concern2.title', label: 'Concern 2 — Title' },
      { key: 'brandStory.madeFor.concern2.desc', label: 'Concern 2 — Description' },
      { key: 'brandStory.madeFor.concern3.title', label: 'Concern 3 — Title' },
      { key: 'brandStory.madeFor.concern3.desc', label: 'Concern 3 — Description' },
      { key: 'brandStory.madeFor.concern4.title', label: 'Concern 4 — Title' },
      { key: 'brandStory.madeFor.concern4.desc', label: 'Concern 4 — Description' },
    ],
  },
  {
    key: 'cta',
    title: 'Brand CTA',
    icon: Megaphone,
    note: 'The "Shop Now" / "Where to Buy" button labels are shared site-wide strings, edited under Settings, not here. The brochure PDF link is fixed in code.',
    texts: [
      { key: 'brandStory.cta.heading', label: 'Heading' },
      { key: 'brandStory.cta.body', label: 'Body Text', long: true },
      { key: 'brandStory.cta.downloadBrochure', label: 'Button — Download Brochure' },
    ],
  },
  {
    key: 'seo',
    title: 'SEO',
    icon: Search,
    note: 'Shown in Google search results and social media link previews — not visible on the page itself.',
    texts: [
      { key: 'seo.brandStory.title', label: 'Meta Title', long: true },
      { key: 'seo.brandStory.description', label: 'Meta Description', long: true },
    ],
  },
];

// The new-design Brand Story (src/pages/brand-story/v2/BrandStoryV2.tsx), page key
// 'brandStoryV2'. Its texts are mostly the same brandStory.* keys as the classic
// page, so an edit here also changes the classic page; the new sections add
// brandV2.* keys and new photos.
const pick = (key: string) => BRAND_STORY_SECTIONS.find((x) => x.key === key)!.texts ?? [];
const BRAND_STORY_V2_SECTIONS: SectionDef[] = [
  {
    key: 'hero',
    title: '1. Hero — brand film',
    icon: Sparkles,
    note: 'The hero photo sits behind the heading; once a film is uploaded (or a silent loop), it plays there instead. "Watch our film" opens it with sound. Upload the film (MP4 under 50 MB) or paste a YouTube link. A wide (landscape) film looks best. Until a film is set, the 3-step routine video is used.',
    images: [{ key: 'brandV2.hero', label: 'Hero photo (shown before the video loads)' }],
    texts: [...pick('hero'), { key: 'brandV2.film.watch', label: 'Button — Watch our film' }, { key: 'brandV2.hero.journey', label: 'Button — Our story' }],
    extra: (
      <div className="grid gap-3 sm:grid-cols-2">
        <VideoField contentKey="brandV2.film.video" label="Brand film" hint="MP4 under 50 MB, or a YouTube link." />
        <VideoField
          contentKey="brandV2.hero.video"
          label="Hero video (optional)"
          hint="Plays behind the hero heading, muted at first; visitors tap Sound on for its audio. MP4 or a YouTube link. Empty = the brand film plays there (or the built-in film cut if no film is set)."
        />
      </div>
    ),
  },
  {
    key: 'founder',
    title: '2. Founder',
    icon: Quote,
    note: "A large boxed portrait that slides in, with the founder's message beside it. Separate paragraphs of the message with a new line. Name and title also show under the photo.",
    images: [
      { key: 'brandV2.founder', label: 'Founder photo (portrait)' },
      { key: 'brandStory.company.productImage', label: 'Used until a founder photo is set' },
    ],
    texts: [
      { key: 'brandV2.founder.label', label: 'Small label' },
      { key: 'brandV2.founder.quote', label: 'Quote (one or two lines)', long: true },
      { key: 'brandV2.founder.message', label: 'Message', long: true },
      { key: 'brandStory.company.signatureName', label: 'Name' },
      { key: 'brandStory.company.signatureCaption', label: 'Title' },
    ],
  },
  {
    key: 'company',
    title: '3. Company profile',
    icon: Building2,
    note: 'The.K Seoul logo branching to the three brand logos, beside the story; a Seoul–Dhaka route, six key facts, a burgundy band of four counting numbers, and the brand family cards.',
    texts: [
      { key: 'brandV2.company.label', label: 'Small label' },
      { key: 'brandV2.company.heading', label: 'Heading' },
      { key: 'brandStory.company.p1', label: 'Intro', long: true },
      { key: 'brandV2.company.tag', label: 'Line under the The.K Seoul logo' },
      { key: 'brandV2.company.routeFrom', label: 'Route — Seoul line' },
      { key: 'brandV2.company.routeTo', label: 'Route — Dhaka line' },
      ...[1, 2, 3, 4, 5, 6].flatMap((n) => [
        { key: `brandV2.company.f${n}l`, label: `Fact ${n} — label` },
        { key: `brandV2.company.f${n}v`, label: `Fact ${n} — value` },
      ]),
      ...[1, 2, 3, 4].flatMap((n) => [
        { key: `brandV2.glance.n${n}`, label: `Number ${n} (e.g. 37+)` },
        { key: `brandV2.glance.l${n}`, label: `Number ${n} — label` },
      ]),
      { key: 'brandV2.company.familyLabel', label: 'Brands — small label' },
      { key: 'brandV2.company.familyHeading', label: 'Brands — heading' },
      { key: 'brandV2.brands.rxk', label: 'Rx.K Seoul — one line' },
      { key: 'brandV2.brands.pickk', label: 'Pick.K Seoul — one line' },
      { key: 'brandV2.brands.medik', label: 'Medi.K Seoul — one line' },
      { key: 'brandV2.brands.here', label: 'Brands — "you are here" tag' },
    ],
  },
  {
    key: 'launch',
    title: 'Launch gallery',
    icon: GalleryHorizontal,
    note: 'Photos and captions are the Home page launch gallery (Website → Home Page → New design → K-Goods Festa gallery). Only the headings are shared here.',
    texts: [
      { key: 'homeV2.launch.label', label: 'Eyebrow Label' },
      { key: 'homeV2.launch.heading', label: 'Heading' },
      { key: 'homeV2.launch.headingEm', label: 'Heading — coloured part' },
      { key: 'homeV2.launch.subtitle', label: 'Subtitle' },
    ],
  },
  {
    key: 'name',
    title: '4. The brand — what Rx.K Seoul means',
    icon: Sparkles,
    note: 'Rx + K + Seoul = the logo: three circles explaining each part, then the Rx.K Seoul logo.',
    texts: [
      { key: 'brandV2.name.label', label: 'Small label' },
      { key: 'brandV2.name.heading', label: 'Heading' },
      { key: 'brandV2.name.lead', label: 'Intro', long: true },
      { key: 'brandV2.name.result', label: 'Line under the logo' },
      ...(['rx', 'k', 'seoul'] as const).flatMap((k) => [
        { key: `brandV2.name.${k}.mark`, label: `${k.toUpperCase()} — big letters` },
        { key: `brandV2.name.${k}.title`, label: `${k.toUpperCase()} — title` },
        { key: `brandV2.name.${k}.text`, label: `${k.toUpperCase()} — text`, long: true },
      ]),
    ],
  },
  {
    key: 'problem',
    title: 'Why we exist (The Problem)',
    icon: AlertTriangle,
    note: 'Numbers count up when they appear.',
    images: [{ key: 'brandStory.problem.image', label: 'Section Photo' }],
    texts: pick('problem'),
  },
  {
    key: 'manifesto',
    title: 'Brand philosophy',
    icon: Quote,
    note: 'One large line that lights up word by word. The last two words are shown in burgundy italic.',
    texts: [
      { key: 'brandV2.manifesto.quote', label: 'Statement' },
      { key: 'brandV2.manifesto.text', label: 'Text under it', long: true },
    ],
  },
  { key: 'solution', title: 'Brand values (Our Solution)', icon: Lightbulb, texts: pick('solution') },
  {
    key: 'journey',
    title: 'Brand promise (Seoul to you)',
    icon: Route,
    texts: [
      { key: 'brandV2.journey.label', label: 'Eyebrow Label' },
      { key: 'brandV2.journey.heading', label: 'Heading' },
      { key: 'brandV2.journey.headingEm', label: 'Heading — coloured part' },
      ...[1, 2, 3, 4, 5].flatMap((n) => [
        { key: `brandV2.journey.step${n}.title`, label: `Step ${n} — Title` },
        { key: `brandV2.journey.step${n}.text`, label: `Step ${n} — Text` },
      ]),
      { key: 'brandV2.journey.verify', label: 'Button' },
    ],
  },
  {
    key: 'madeFor',
    title: 'Made for Bangladesh',
    icon: MapPin,
    images: [1, 2, 3, 4].map((n) => ({ key: `brandV2.concern${n}`, label: `Concern ${n} photo` })),
    texts: pick('madeFor'),
  },
  {
    key: 'cta',
    title: 'Closing call',
    icon: Megaphone,
    images: [{ key: 'brandV2.cta', label: 'Photo' }],
    texts: [...pick('cta'), { key: 'brandV2.cta.shop', label: 'Button — Shop' }],
  },
  { key: 'seo', title: 'SEO', icon: Search, note: 'Shown in Google search results and social media link previews — not visible on the page itself.', texts: pick('seo') },
];

// Opens on whichever design is live (Settings → Design); both stay editable.
export default function AdminBrandStoryPage() {
  const design = useSiteContentValue('site.design', 'en', 'classic');
  const [tab, setTab] = useState<'new' | 'classic' | null>(null);
  const active = tab ?? (design === 'classic' ? 'classic' : 'new');
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1.5">
        {(
          [
            ['new', 'New design'],
            ['classic', 'Classic design'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`text-[12.5px] font-semibold px-3.5 py-2 rounded-full cursor-pointer transition-colors ${active === key ? 'bg-primary-800 text-white' : 'bg-surface-muted text-ink-faint hover:text-ink'}`}
          >
            {label}
          </button>
        ))}
      </div>
      {active === 'new' ? (
        <SectionPageEditor key="brandStoryV2" pageKey="brandStoryV2" title="Brand Story — new design" sections={BRAND_STORY_V2_SECTIONS} />
      ) : (
        <SectionPageEditor key="brandStory" pageKey="brandStory" title="Brand Story — classic design" sections={BRAND_STORY_SECTIONS} />
      )}
    </div>
  );
}
